import { DateTime, Duration } from 'luxon';
import { TimeRangeSelector } from '@appd/uik/@future';

/**
 * Checks if the provided value is a valid ISO 8601 duration or a valid ISO 8601 UTC date-time.
 * This function accepts durations (e.g., 'PT6H' for 6 hours) and date-times (e.g., '2020-01-01T12:00:00Z').
 * It returns true if the value is correctly formatted as a duration or date-time, or if it is undefined or null.
 *
 * @param {string | undefined | null} value - The value to check.
 * @param {boolean} isRequired - Specifies if the value is required to be non-empty.
 * @returns {boolean} True if the value is a valid ISO 8601 duration or date-time, or if it is undefined or null.
 */
const isValidDurationOrDate = (value: string | undefined | null, isRequired: boolean = false) => {
  if (!isRequired && (value === undefined || value === null || value === '')) {
    return true; // Allow undefined, null, or empty values if isNullable
  }

  // Try to parse the value as a duration
  const duration = Duration.fromISO(value as string);
  if (duration.isValid) {
    return true; // Correctly identifies valid positive or negative durations
  }

  // Try to parse the value as a date-time
  const dateTime = DateTime.fromISO(value as string, { zone: 'UTC' });
  if (dateTime.isValid) {
    return true; // Correctly identifies valid date-times
  }

  return false;
};

/**
 * Checks if the given time range is valid considering both ISO 8601 durations and absolute dates.
 *
 * @param {TimeRangeSelector.TimeRange.Resolved | undefined} timeRange - The time range to validate.
 * @returns {boolean} True if the time range is valid, false otherwise.
 */
const isValidTimeRange = (timeRange: TimeRangeSelector.TimeRange.Resolved | undefined): boolean =>
  timeRange != null && isValidDurationOrDate(timeRange.since, true) && isValidDurationOrDate(timeRange.until);

export default isValidTimeRange;
